import * as React from 'react';
import { cn } from '../../utils';

type StatusVariant =
  // Quote / Order
  | 'draft' | 'pending' | 'confirmed' | 'cancelled' | 'in_review'
  | 'waiting' | 'waiting_for_offer' | 'under_review'
  // Order execution
  | 'planned' | 'in_progress' | 'completed' | 'in_transit' | 'delivered'
  // Finance
  | 'invoiced' | 'settled' | 'sent' | 'received' | 'partial' | 'paid'
  | 'overdue' | 'void' | 'posted' | 'reimbursed' | 'rejected' | 'submitted'
  // Generic
  | 'approved' | 'assigned' | 'active' | 'inactive' | 'maintenance'
  | 'expired' | 'expiring'
  | string;

/**
 * Which token family a status draws from. The families are in
 * `@rr/ui/tokens` (`--status-<family>-bg|text|border`) and each has a value
 * in both `:root` and `.dark`, which is the whole point: a chip that picked
 * its colours itself would be a light-mode chip wherever it landed.
 *
 * Several statuses share a family on purpose — `confirmed`, `completed` and
 * `approved` all mean "this went through", and giving each its own green
 * would be three shades the reader has to tell apart for no gain.
 */
const FAMILY: Record<string, string> = {
  draft: 'draft',
  void: 'void',
  inactive: 'void',
  archived: 'void',

  pending: 'pending',
  partial: 'partial',
  full: 'confirmed',
  maintenance: 'pending',
  expiring: 'pending',

  waiting: 'waiting',
  waiting_for_offer: 'waiting',
  // Planned means nothing has happened yet, so it reads neutral — and it
  // keeps its distance from `in_progress`, which is the next step along.
  planned: 'draft',
  sent: 'sent',
  received: 'sent',
  under_review: 'review',
  in_review: 'review',

  confirmed: 'confirmed',
  approved: 'confirmed',
  active: 'confirmed',
  assigned: 'confirmed',
  completed: 'delivered',
  delivered: 'delivered',
  paid: 'paid',
  done: 'paid',
  posted: 'posted',

  in_transit: 'transit',
  in_progress: 'transit',
  submitted: 'transit',

  invoiced: 'invoiced',
  reimbursed: 'reimbursed',
  settled: 'settled',

  cancelled: 'cancelled',
  rejected: 'cancelled',
  expired: 'cancelled',
  suspended: 'cancelled',
  retired: 'cancelled',
  overdue: 'overdue',
};

/** English fallbacks. Call sites that have a translator pass `label`. */
const LABEL: Record<string, string> = {
  draft: 'Draft', pending: 'Pending', waiting: 'Waiting',
  waiting_for_offer: 'Waiting', under_review: 'In Review', in_review: 'In Review',
  confirmed: 'Confirmed', approved: 'Approved', active: 'Active',
  completed: 'Completed', delivered: 'Delivered', paid: 'Paid', posted: 'Posted',
  reimbursed: 'Reimbursed', assigned: 'Assigned', cancelled: 'Cancelled',
  overdue: 'Overdue', rejected: 'Rejected', in_transit: 'In Transit',
  in_progress: 'In Progress', submitted: 'Submitted', invoiced: 'Invoiced',
  sent: 'Sent', received: 'Received', partial: 'Partial', full: 'Full', settled: 'Settled',
  planned: 'Planned', void: 'Void', inactive: 'Inactive',
  maintenance: 'Maintenance', expired: 'Expired', expiring: 'Expiring',
  archived: 'Archived', suspended: 'Suspended', retired: 'Retired',
};

export interface StatusChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  status: StatusVariant;
  label?: string;
  size?: 'xs' | 'sm' | 'md';
}

export function StatusChip({ status, label, size = 'md', className, style, ...props }: StatusChipProps) {
  // Backend enums arrive uppercase (`IN_PROGRESS`), quote statuses lowercase;
  // normalise so a caller can pass whichever it has.
  const key = String(status).trim().toLowerCase().replace(/[\s-]+/g, '_');
  // An unmapped status takes the neutral family rather than no colour at all,
  // so a new backend value renders as a plain chip instead of an unstyled word.
  const family = FAMILY[key] ?? 'draft';
  const displayLabel =
    label || LABEL[key] || String(status).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const padClass =
    size === 'xs' ? 'px-1.5 py-0 text-[10px]' : size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-0.5 text-[11.5px]';

  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border font-semibold leading-[18px] tracking-[.01em]',
        padClass,
        className,
      )}
      style={{
        background: `hsl(var(--status-${family}-bg))`,
        color: `hsl(var(--status-${family}-text))`,
        borderColor: `hsl(var(--status-${family}-border))`,
        ...style,
      }}
      {...props}
    >
      {displayLabel}
    </span>
  );
}

export type { StatusVariant };
